import React, { useMemo, useState } from 'react';
import { Panel, PanelTheme, PanelBorderRadius, PanelBorderStyle } from '@apply/components';
import { applicationInsights } from '../../../../app/ApplicationInsights';
import '../../scss/_homepanels.scss';
import { DrawApi } from '@apply/api';
import { DrawType } from '@apply/constants';
import { IDashboardDrawBudget } from '@apply/api/src/api/DrawApi';
import { RequestDrawModalV2 } from '@apply/ui';
import { useAuthentication } from '../../../LoginPage';
import { LoanDrawCard } from './LoanDrawCard/LoanDrawCard';
import { ProgressSpinner } from '../../../../../../../packages/components/src/components/controls/ProgressSpinner';


export interface LoanDrawPanelProps {
    dashboardDrawBudgets: IDashboardDrawBudget[];
    loading: boolean;
}

export const LoanDrawPanel: React.FC<LoanDrawPanelProps> = ({ dashboardDrawBudgets, loading }) => {
    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);
    const [modalDashboarDrawBudget, set_modalDashboarDrawBudget] = useState<IDashboardDrawBudget | undefined>(undefined);

    const { authentication } = useAuthentication();
    const borrowerName = useMemo(() => `${authentication?.user?.firstName} ${authentication?.user?.lastName}`, [authentication]);

    const handleCloseEvent = () => {
        set_modalDashboarDrawBudget(undefined);
    };

    const submitDrawRequest = async ({
        drawBudgetId,
        drawType,
        fontId,
        ipAddress,
    }: {
        drawBudgetId: number;
        drawType: DrawType;
        fontId: number | undefined;
        ipAddress: string | undefined;
    }) => {
        // Wait for draw to be added before processing or redirecting
        await drawApi.addDraw({ drawBudgetId, drawType, fontId, ipAddress });

        if (!dashboardDrawBudgets) return;
        const dataItem = dashboardDrawBudgets.find((p) => p.drawBudgetId === drawBudgetId);

        if (!dataItem) return;
        dataItem.canAddDraw = false;

        if (dataItem.latestDrawType == DrawType.Manual) {
            dataItem.needsBorrowerSignature = true;
        }
    };

    return (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Panel
                theme={PanelTheme.Blue}
                borderRadius={PanelBorderRadius.Medium}
            >
                <h3>Loan Draw</h3>
                <div className='panelContent loanPanels'>
                    {!dashboardDrawBudgets?.length ? (
                        <Panel
                            theme={PanelTheme.White}
                            borderStyle={PanelBorderStyle.Solid}
                            borderRadius={PanelBorderRadius.Small}
                            padding={16}
                        >
                            {loading ? (
                                <ProgressSpinner />
                            ) : (
                                <span className='no-loan-panel'>You currently have no loans outstanding</span>
                            )}
                        </Panel>
                    ) : (
                        dashboardDrawBudgets?.map((dashboardDrawBudget) => {
                            return (
                                <LoanDrawCard
                                    key={dashboardDrawBudget.drawBudgetId}
                                    dashboardDrawBudget={dashboardDrawBudget}
                                    set_modalDashboarDrawBudget={set_modalDashboarDrawBudget}
                                />
                            );
                        })
                    )}
                </div>
            </Panel>
            <RequestDrawModalV2
                drawBudgetId={modalDashboarDrawBudget?.drawBudgetId}
                currentAccountNumber={modalDashboarDrawBudget?.bankAccountLast4Digits?.toString()}
                isOutOfBalance={modalDashboarDrawBudget?.isOutOfBalance ?? false}
                isOutOfBalanceWaived={modalDashboarDrawBudget?.isOutOfBalanceWaived ?? false}
                percentFunded={modalDashboarDrawBudget?.percentFunded}
                open={modalDashboarDrawBudget !== undefined}
                submitDrawRequest={submitDrawRequest}
                handleCloseEvent={handleCloseEvent}
                applicationInsights={applicationInsights}
                borrowerName={borrowerName}
            />
        </div>
    );
};
